import {
  LineChart,
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { format } from 'date-fns'

interface MetricsChartProps {
  data: Array<{
    date: string
    compliance_score: number
    violations: number
    scans: number
  }>
}

export default function MetricsChart({ data }: MetricsChartProps) {
  const formatDate = (dateStr: string) => {
    return format(new Date(dateStr), 'MMM dd')
  }

  const formatPercent = (value: number) => {
    return `${Math.round(value * 100)}%`
  }

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="text-sm font-medium text-gray-900 mb-2">
            {formatDate(label)}
          </p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {
                entry.dataKey === 'compliance_score'
                  ? formatPercent(entry.value)
                  : entry.value
              }
            </p>
          ))}
        </div>
      )
    }
    return null
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900">
          Compliance Trends
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Platform-wide metrics over the last 7 days
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Compliance Score Trend */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">
            Compliance Score Trend
          </h4>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={data}>
              <defs>
                <linearGradient id="colorCompliance" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                stroke="#6B7280"
                fontSize={12}
              />
              <YAxis
                tickFormatter={formatPercent}
                stroke="#6B7280"
                fontSize={12}
                domain={[0, 1]}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="compliance_score"
                stroke="#3B82F6"
                strokeWidth={2}
                fill="url(#colorCompliance)"
                name="Compliance Score"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Violations & Scans */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 mb-3">
            Violations & Scan Activity
          </h4>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                stroke="#6B7280"
                fontSize={12}
              />
              <YAxis stroke="#6B7280" fontSize={12} />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ fontSize: '12px' }}
                iconType="line"
              />
              <Line
                type="monotone"
                dataKey="violations"
                stroke="#EF4444"
                strokeWidth={2}
                dot={{ fill: '#EF4444', r: 4 }}
                activeDot={{ r: 6 }}
                name="Violations"
              />
              <Line
                type="monotone"
                dataKey="scans"
                stroke="#10B981"
                strokeWidth={2}
                dot={{ fill: '#10B981', r: 4 }}
                activeDot={{ r: 6 }}
                name="Scans"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="mt-6 pt-4 border-t border-gray-200 grid grid-cols-3 gap-4">
        <div className="text-center">
          <p className="text-sm text-gray-600">Avg Compliance</p>
          <p className="text-lg font-bold text-gray-900">
            {data.length > 0
              ? formatPercent(
                  data.reduce((sum, d) => sum + d.compliance_score, 0) / data.length
                )
              : '—'}
          </p>
        </div>
        <div className="text-center">
          <p className="text-sm text-gray-600">Total Violations</p>
          <p className="text-lg font-bold text-red-600">
            {data.reduce((sum, d) => sum + d.violations, 0)}
          </p>
        </div>
        <div className="text-center">
          <p className="text-sm text-gray-600">Total Scans</p>
          <p className="text-lg font-bold text-green-600">
            {data.reduce((sum, d) => sum + d.scans, 0)}
          </p>
        </div>
      </div>
    </div>
  )
}